import React, { Suspense, useRef, useEffect, useState, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { gsap } from "gsap";

// Preload
useGLTF.preload("/3dModel/injection1.glb");

const Model = ({ scaleFactor, distance }) => {
  const group = useRef();
  const { scene } = useGLTF("/3dModel/Injection1.glb");

  // clone for safety
  const clonedScene = useMemo(() => scene.clone(true), [scene]);

  const cap = useRef();
  const screw = useRef();
  const tl = useRef();

  useEffect(() => {
    if (!group.current || !clonedScene) return;

    const capMesh = clonedScene.getObjectByName("cap");
    const screwMesh = clonedScene.getObjectByName("screw");

    if (capMesh) cap.current = capMesh;
    if (screwMesh) screw.current = screwMesh;

    group.current.scale.set(scaleFactor, scaleFactor, scaleFactor);
    group.current.rotation.set(0.25, 0.25, 0.05);

    // Timeline
    if (cap.current && screw.current) {
      tl.current = gsap.timeline({
        paused: true,
        defaults: { ease: "power2.inOut" }
      });

      tl.current.to(cap.current.position, { x: -distance, duration: 1 }, 0);
      tl.current.to(screw.current.position, { x: distance, duration: 1 }, 0);
    }

    // Scroll triggers (no freeze)
    const handleTrigger = (e) => {
      if (!tl.current) return;
      const direction = e.detail?.direction || "forward";
      if (direction === "forward") tl.current.play();
      else tl.current.reverse();
    };

    window.addEventListener("triggerModelAnimation", handleTrigger);

    return () => {
      window.removeEventListener("triggerModelAnimation", handleTrigger);
      if (tl.current) tl.current.kill();
    };
  }, [clonedScene, distance, scaleFactor]);

  // Mobile + Tablet scroll-based progress
  const lastProgress = useRef(0);
  const lastScrollY = useRef(0);

  useFrame(() => {
    if (!cap.current || !screw.current) return;

    const width = window.innerWidth;

    // Desktop uses play/reverse only
    if (width >= 1024) return;

    const scrollTop = window.scrollY;
    const docHeight = document.body.scrollHeight - window.innerHeight;
    const progress = Math.min(Math.max(scrollTop / docHeight, 0), 1);

    lastScrollY.current = scrollTop;

    if (Math.abs(progress - lastProgress.current) > 0.01) {
      lastProgress.current = progress;

      cap.current.position.x = -distance * progress;
      screw.current.position.x = distance * progress;
    }
  });

  // Cleanup
  useEffect(() => {
    return () => {
      if (clonedScene) {
        clonedScene.traverse((obj) => {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach((mat) => mat.dispose());
            } else {
              obj.material.dispose();
            }
          }
        });
      }
    };
  }, [clonedScene]);

  return <primitive ref={group} object={clonedScene} />;
};

export default function ScrollSeparationModel() {
  const [cameraZ, setCameraZ] = useState(10);
  const [scaleFactor, setScaleFactor] = useState(1.5);
  const [distance, setDistance] = useState(1.7);

  // Device-based settings
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 640) {
        setCameraZ(13);
        setScaleFactor(1.2);
        setDistance(1.3);
      } else if (width < 1024) {
        setCameraZ(11);
        setScaleFactor(0.7);
        setDistance(1.2);
      } else {
        setCameraZ(10);
        setScaleFactor(1.7);
        setDistance(1);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Canvas
      camera={{ position: [0, 0, cameraZ], fov: 40 }}
      style={{ width: "100%", height: "100%" }}
      gl={{
        antialias: true,
        alpha: true,
        powerPreference: "high-performance",
        stencil: false,
        depth: true
      }}
      dpr={[1, 2]}
      performance={{ min: 0.5 }}
      frameloop="always"
    >
      <ambientLight intensity={0.8} />
      <directionalLight position={[5, 5, 5]} />

      <Suspense fallback={null}>
        <Model scaleFactor={scaleFactor} distance={distance} />
      </Suspense>
    </Canvas>
  );
}
